import React, { useEffect, useMemo, useState } from 'react'
import DOMPurify from 'dompurify'
import { documentBodyAsFile } from 'src/adapter/AttachmentAdapter'
import { isEvidenceFile } from 'src/components/ModalForms/Evidence/evidenceUtils'
import { isViewable } from 'src/components/ModalForms/Evidence/ViewEvidenceForm/ViewEvidenceForm.utils'
import { useFileTypeIcon } from 'src/components/ModalForms/FileType/useFileTypeIcon'
import { MIME_TYPE } from 'src/Utils/globalEnums'
import FileSaver from 'file-saver'
import { InlineDocument } from 'src/components/Reusable/Document/DocumentViewer.components'
import { TableListLoader } from 'src/components/Reusable/ContentLoaders/TableListLoader'
import { Markup } from 'src/components/Reusable/Text/Markup'
import { ManualEvidenceBody } from 'src/pages/Evidence/evidence.components'
import { getIsLongMarkdown } from 'src/pages/Evidence/evidence.helpers'
import { EvidenceConfigType } from 'src/pages/Evidence/evidence.constants'
import palette from 'src/designSystem/variables/palette'
import {
  BASIC_DROPDOWN_ALIGN,
  BASIC_DROPDOWN_DROP,
} from 'src/components/Reusable/BasicDropdown/BasicDropdown.constants'
import { BasicDropdown } from 'src/components/Reusable/BasicDropdown/BasicDropdown'
import { BasicDropdownItem } from 'src/components/Reusable/BasicDropdown/BasicDropdown.types'
import {
  EvidenceFileInfoContainer,
  EvidenceFileName,
  EvidenceMarkdown,
  EvidenceMarkdownContainer,
} from 'src/pages/Evidence/evidence.styles'
import { AutomaticEvidenceBody } from './AutomaticEvidence/AutomaticEvidenceBody'
import {
  EvidenceBodiesContainer,
  EvidenceShowTabContainer,
} from './EvidenceBody.styles'

export const EvidenceBody = ({
  body,
  caption,
  mimeType,
  isAutomated,
  preview,
  pageNumber,
  isModal,
}: {
  body: string | Uint8Array | null
  caption: string
  mimeType: string
  isAutomated: boolean
  preview?: React.ReactNode
  pageNumber?: number
  isModal?: boolean
}): JSX.Element => {
  const [file, setFile] = useState<File | null>(null)

  useEffect(() => {
    if (body && mimeType) {
      const file = documentBodyAsFile(body, caption, mimeType)
      setFile(file)
    }
  }, [body, setFile, mimeType, caption])

  const showAutomated = isAutomated && body instanceof Uint8Array

  const isFile = isEvidenceFile(mimeType)
  const FileTypeIcon = useFileTypeIcon({ mime: mimeType })
  const isLink = mimeType === MIME_TYPE.TEXT_URI_LIST
  const isHtml = mimeType === MIME_TYPE.TEXT_HTML
  const isLongMarkdown = getIsLongMarkdown(body, mimeType)
  const viewable = isViewable(mimeType)

  const fileInfoComponent = useMemo(() => {
    if (!file) return <></>
    return (
      <EvidenceFileInfoContainer
        onClick={() => FileSaver.saveAs(file)}
        style={{ width: '100%' }}
      >
        {preview}
        <FileTypeIcon />
        <EvidenceFileName>{file.name}</EvidenceFileName>
      </EvidenceFileInfoContainer>
    )
  }, [file, preview, FileTypeIcon])

  return (
    <>
      {body && mimeType ? (
        <EvidenceShowTabContainer>
          {showAutomated ? (
            // This is protobuf content, not necessarily all automated evidence, for example, pdfs from automated
            // evidence are viewed as inline pdfs.
            <AutomaticEvidenceBody body={body} />
          ) : (
            <>
              {isFile ? (
                // Avoid allowing file preview click if not ready/available
                <>
                  {file ? (
                    <>
                      {viewable ? (
                        <InlineDocument
                          file={file}
                          body={body}
                          isEvidenceShowPage
                          pageNumber={pageNumber}
                          downloaderComponent={fileInfoComponent}
                          isModal={isModal}
                        />
                      ) : (
                        fileInfoComponent
                      )}
                    </>
                  ) : (
                    <TableListLoader />
                  )}
                </>
              ) : isHtml ? (
                <div style={{ overflow: 'auto' }}>
                  <Markup
                    rawHtml={DOMPurify.sanitize(body as string)}
                    isHtml={true}
                  />
                </div>
              ) : isLongMarkdown ? (
                <EvidenceMarkdownContainer>
                  <EvidenceMarkdown markdown={body as string} />
                </EvidenceMarkdownContainer>
              ) : (
                <ManualEvidenceBody body={body as string} isLink={isLink} />
              )}
            </>
          )}
        </EvidenceShowTabContainer>
      ) : (
        <TableListLoader />
      )}
    </>
  )
}

export const EvidenceBodies = ({
  caption,
  configs,
  documentOffset,
  pageNumber,
  preview,
  isModal,
}: {
  caption: string
  configs: EvidenceConfigType[] | null
  documentOffset?: number
  pageNumber?: number
  preview?: React.ReactNode
  isModal?: boolean
}): JSX.Element | null => {
  const [selectedIndex, setSelectedIndex] = useState<number>(
    documentOffset || 0,
  )

  const filteredConfigs = useMemo(
    () => configs?.filter((config) => !!config.body) ?? [],
    [configs],
  )
  const items = useMemo(() => {
    if (!filteredConfigs) return null
    const namedFiles = filteredConfigs.every((config) => config.name)
    return filteredConfigs
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(
        (config, index) =>
          ({
            name: namedFiles ? `${config.name}` : `${caption} ${index + 1}`,
            value: index,
          } as BasicDropdownItem),
      )
  }, [filteredConfigs, caption])

  const selectedConfig = useMemo(
    () => filteredConfigs?.[selectedIndex],
    [filteredConfigs, selectedIndex],
  )

  if (!filteredConfigs || !items) {
    return <TableListLoader />
  }

  return (
    <EvidenceBodiesContainer>
      {filteredConfigs.length > 1 && (
        <BasicDropdown
          selectedItem={items[selectedIndex]}
          items={items}
          onSelect={(item: BasicDropdownItem) => {
            setSelectedIndex(item.value as number)
          }}
          borderColor={palette.neutral[200]}
          size="medium"
          drop={BASIC_DROPDOWN_DROP.DOWN}
          align={BASIC_DROPDOWN_ALIGN.END}
        />
      )}
      {selectedConfig ? (
        <EvidenceBody
          caption={caption}
          body={selectedConfig.body}
          mimeType={selectedConfig.mimeType}
          isAutomated={selectedConfig.isAutomated}
          preview={preview}
          pageNumber={pageNumber}
          isModal={isModal}
        />
      ) : (
        <TableListLoader />
      )}
    </EvidenceBodiesContainer>
  )
}
